import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useLoginModalContext } from '@contexts/LoginModalContext';

interface AutoLoginModalOptions {
  delay?: number;
  enabled?: boolean;
  localStorageKey?: string;
}

export const useAutoLoginModal = (options: AutoLoginModalOptions = {}) => {
  const {
    delay: defaultDelay = 10000,
    enabled = true,
    localStorageKey = 'auto_login_modal_enabled'
  } = options;

  const { isAuthenticated, isLoading } = useAuth();
  const { isLoginModalActive } = useLoginModalContext();
  const [showModal, setShowModal] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const isUserInteractingRef = useRef(false);

  const getDelay = useCallback(() => {
    const storedDelay = localStorage.getItem('auto_login_modal_delay');
    return storedDelay ? parseInt(storedDelay, 10) * 1000 : defaultDelay;
  }, [defaultDelay]);

  const isAutoLoginEnabled = useCallback(() => {
    const stored = localStorage.getItem(localStorageKey);
    return stored !== 'false';
  }, [localStorageKey]);

  const setAutoLoginEnabled = (enabled: boolean) => {
    localStorage.setItem(localStorageKey, enabled.toString());
  };

  useEffect(() => {
    const handleUserInteraction = () => {
      isUserInteractingRef.current = true;
      setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 5000);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction, true);
      });
    };
  }, []);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (
      isAuthenticated ||
      isLoading ||
      !enabled ||
      !isAutoLoginEnabled() ||
      hasShownModal ||
      isUserInteractingRef.current ||
      isLoginModalActive
    ) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      if (
        !isAuthenticated &&
        !isLoading &&
        enabled &&
        isAutoLoginEnabled() &&
        !hasShownModal &&
        !isUserInteractingRef.current &&
        !isLoginModalActive
      ) {
        setShowModal(true);
        setHasShownModal(true);
      }
    }, getDelay());

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, isLoading, enabled, hasShownModal, isLoginModalActive, getDelay, isAutoLoginEnabled, defaultDelay, localStorageKey]);

  useEffect(() => {
    if (isAuthenticated) {
      setHasShownModal(false);
    }
  }, [isAuthenticated]);

  const closeModal = () => {
    setShowModal(false);
  };

  const disableAutoLogin = () => {
    setAutoLoginEnabled(false);
    setShowModal(false);
  };

  return {
    showModal,
    closeModal,
    disableAutoLogin,
    isAutoLoginEnabled: isAutoLoginEnabled(),
    setAutoLoginEnabled
  };
};
